/*@flow*/
import type { LocationPickerContext, StateRecord } from './types';
import transformPlace from './transformPlace';
import NonEmptyObject from 'lib/NonEmptyObject';
import Blank from 'lib/Blank';
import waitTill from 'lib/waitTill';
import wellknown from 'wellknown';
import * as I from 'immutable';

type CenterT = { lat: number, lon: number };

function parseLatlon(latlon: [number, number] | CenterT) {
  let lat, lon;
  if (Array.isArray(latlon)) {
    lat = latlon[0];
    lon = latlon[1];
  } else {
    lat = latlon.lat;
    lon = latlon.lon;
  }
  return {
    lat,
    lon,
  };
}

function getDrawingGeoJSON(s: StateRecord) {
  const handler = s.drawingHandler;
  if (!handler || !handler.isValid()) return;
  return handler.polygon.toGeoJSON();
}

function getDrawingGeom(s: StateRecord) {
  const geojson = getDrawingGeoJSON(s);
  return geojson ? wellknown.stringify(geojson) : s.theGeom;
}

const commands = (ctx: LocationPickerContext): {|
  remove: (path: string) => void,
  resetGeom: () => void,
  set: (path: string, value: any) => void,
  setCenter: (center: CenterT) => void,
  setFullAddress: (fullAddress: string) => void,
  setLocation: (latlon: [number, number] | CenterT) => void,
  setPlace: (googPlace: $Shape<Goog$PlaceT>) => void | string,
  setShape: () => void,
  setTheGeom: (theGeom: string) => void,
  setTheGeomFromLatlon: (latlon: [number, number] | CenterT) => void,
  toggleDraw: () => void,
|} => ({
  setFullAddress(fullAddress: string) {
    if (fullAddress !== ctx.state.fullAddress) {
      ctx.state.onAddressChange(fullAddress);
    }
    commands(ctx).set('fullAddress', fullAddress);
  },
  setCenter(center: CenterT) {
    commands(ctx).set('center', center);
  },
  setTheGeom(theGeom: string) {
    if (theGeom !== ctx.state.theGeom) {
      ctx.state.onGeomChange(theGeom);
    }
    commands(ctx).set('theGeom', theGeom);
  },
  setTheGeomFromLatlon(latlon: [number, number] | CenterT) {
    const { lat, lon } = parseLatlon(latlon);
    const theGeom = `POINT (${lon} ${lat})`;
    commands(ctx).setTheGeom(theGeom);
  },
  toggleDraw() {
    const theGeom = getDrawingGeom(ctx.state);
    theGeom && commands(ctx).setTheGeom(theGeom);

    const isReady = ctx.state.mode === 'READY';
    const type = isReady ? 'ENABLE_DRAWING' : 'DISABLE_DRAWING';
    ctx.dispatch({ type });
  },
  resetGeom() {
    const { centerOrig, theGeomOrig } = ctx.state;
    theGeomOrig && commands(ctx).set('theGeom', theGeomOrig);
    centerOrig && commands(ctx).setCenter(centerOrig);
  },
  setLocation(latlon: [number, number] | CenterT) {
    commands(ctx).setCenter(parseLatlon(latlon));
    commands(ctx).setTheGeomFromLatlon(latlon);
  },
  setPlace(googPlace: $Shape<Goog$PlaceT>) {
    if (Blank.is(googPlace)) return '<empty>';

    // Transform googPlace to our 'place'.
    const place = transformPlace(googPlace);
    if (Blank.is(place)) return '<bad place>';

    const { center, theGeom, fullAddress } = place;

    // If the form theGeom is missing, set it, along with the center...
    const formTheGeom = ctx.state.theGeom;
    commands(ctx).setFullAddress(fullAddress);

    if (!formTheGeom && theGeom && NonEmptyObject.is(center)) {
      commands(ctx).set('theGeomOrig', theGeom);
      commands(ctx).set('centerOrig', center);
      commands(ctx).setLocation(center);
    }
  },
  setShape() {
    const { theGeom } = ctx.state;
    const geojson = wellknown.parse(theGeom);

    if (!geojson) return;
    let shape;
    const type = geojson.type;

    const DEFAULT_STROKE_COLOR = '#EE7B2C';
    const getPolygon = (color = DEFAULT_STROKE_COLOR) =>
      I.fromJS({
        type: 'polygon',
        geojson,
        active: true,
        stroke: true,
        fillColor: '#ababab85',
        color,
        weight: 2,
        opacity: 0.8,
        fillOpacity: 0.7,
        fill: true,
        clickable: false,
      });

    const getPoint = () => {
      const [lon, lat] = geojson.coordinates;
      return I.fromJS({
        type: 'circlemarker',
        radius: 7,
        fillColor: 'red',
        clickable: false,
        latlon: [lat, lon],
      });
    };

    if (type === 'Point') {
      shape = getPoint();
    } else if (type === 'Polygon' || type === 'MultiPolygon') {
      shape = getPolygon();
    }
    if (!shape) return;

    // Create a deck for shape.
    if (type === 'Point') {
      commands(ctx).remove('decks.polygon');
      commands(ctx).set('decks.point', shape);
    } else {
      commands(ctx).remove('decks.point');
      commands(ctx).set('decks.polygon', shape);
    }

    // If the shape is a polygon, fit the map to its bounds.
    if (type === 'Polygon' || type === 'MultiPolygon') {
      waitTill(() => window.propertyMap).then(() => {
        window.propertyMap.fitBounds(L.geoJson(geojson).getBounds());
      });
    }
  },
  set(path: string, value: any) {
    ctx.dispatch({
      type: 'SET_RECORD',
      payload: { path, value },
    });
  },
  remove(path: string) {
    commands(ctx).set('', v => v.deleteIn(path.split('.')));
  },
});

export default commands;
